import { useState, useEffect } from "react";
import { db } from "./Firebase";
import { collection, query, onSnapshot, updateDoc, doc, deleteDoc } from "firebase/firestore";
import { FaCheck, FaTimes, FaTrashAlt } from "react-icons/fa";
import { Button, Dropdown, Form } from "react-bootstrap";
import 'bootstrap/dist/css/bootstrap.min.css';
import Navbar from './Navbar';

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [sortOrder, setSortOrder] = useState("desc");
  const [deliveryTime, setDeliveryTime] = useState("");

  useEffect(() => {
    const q = query(collection(db, "Billing"));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const ordersArray = [];
      querySnapshot.forEach((doc) => {
        ordersArray.push({ ...doc.data(), id: doc.id });
      });
      setOrders(ordersArray);
      setFilteredOrders(ordersArray);
    });
    return () => unsubscribe();
  }, []);

  const handleConfirmOrder = async (orderId) => {
    await updateDoc(doc(db, "Billing", orderId), {
      status: "confirmed",
      estimatedDeliveryTime: "2-3 hrs",
    });
  };

  const handleDeleteOrder = async (orderId) => {
    await deleteDoc(doc(db, "Billing", orderId));
  };

  const handleFilterOrders = (status) => {
    setFilteredOrders(status === "all" ? orders : orders.filter(order => order.status === status));
  };

  const handleSortOrders = () => {
    const sortedOrders = [...filteredOrders].sort((a, b) => {
      return sortOrder === "asc" ? a.totalAmount - b.totalAmount : b.totalAmount - a.totalAmount;
    });
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    setFilteredOrders(sortedOrders);
  };

  const handleUpdateDeliveryTime = async (orderId) => {
    if (!deliveryTime.trim()) return;
    await updateDoc(doc(db, "Billing", orderId), { estimatedDeliveryTime: deliveryTime });
    setDeliveryTime("");
  };

  const handleUpdatePaymentStatus = async (orderId, status) => {
    await updateDoc(doc(db, "Billing", orderId), { paymentStatus: status });
  };

  return (
    <>
      <Navbar />
      <div className=" mt-5 pt-5" style={{ background: 'linear-gradient(to right, #1e3c72, #2a5298)', minHeight: '100vh', color: '#fff', padding: '2rem', borderRadius: '10px' }}>
        <h1 className="text-center mb-4 display-4 fw-bold text-light">
          Admin - Order Management
        </h1>

        <div className="d-flex flex-wrap justify-content-between mb-4">
          <input
            type="text"
            className="form-control w-100 w-md-50 mb-3 mb-md-0"
            placeholder="Search by Order ID"
            onChange={(e) => {
              const searchQuery = e.target.value.toLowerCase();
              setFilteredOrders(orders.filter((order) => order.id.toString().includes(searchQuery)));
            }}
            style={{ maxWidth: '400px', backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid #ccc' }}
          />

          <div className="d-flex flex-wrap gap-2">
            <Button variant="light" onClick={() => handleFilterOrders("all")}>All</Button>
            <Button variant="success" onClick={() => handleFilterOrders("confirmed")}>Confirmed</Button>
            <Button variant="danger" onClick={() => handleFilterOrders("pending")}>Pending</Button>
            <Dropdown>
              <Dropdown.Toggle variant="info" id="dropdown-sort">
                Sort by Total
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={handleSortOrders}>
                  {sortOrder === "asc" ? "Descending" : "Ascending"}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {filteredOrders.length === 0 ? (
          <p className="text-center text-warning">No orders found.</p>
        ) : (
          <div className="row justify-content-center">
            <div className="col-md-10">
              {filteredOrders.map((order) => (
                <div
                  key={order.id}
                  className="mb-4 p-4 rounded shadow"
                  style={{
                    background: order.status === "confirmed" ? "#28a74533" : "#dc354533",
                    borderLeft: `5px solid ${order.status === "confirmed" ? "#28a745" : "#dc3545"}`,
                  }}
                >
                  <h5 className="fw-bold text-white">Order #{order.id}</h5>
                  <p>Status: <strong>{order.status}</strong></p>
                  <p>Total: <strong>₹{order.totalAmount}</strong></p>
                  <p>Payment Method: <strong>{order.paymentMethod === "gpay" ? "Google Pay (UPI)" : "Cash on Delivery"}</strong></p>
                  <p>Payment Status: <strong>{order.paymentStatus || "N/A"}</strong></p>
                  <p>Estimated Delivery Time: <strong>{order.estimatedDeliveryTime || "N/A"}</strong></p>
                  <p>Order Placed: <strong>{order.orderDate ? new Date(order.orderDate.seconds * 1000).toLocaleString() : "N/A"}</strong></p>

                  <h6 className="fw-bold mt-3">Ordered Items:</h6>
                  <ul className="ps-3">
                    {order.items?.length > 0 ? (
                      order.items.map((item, i) => (
                        <li key={i}><strong>{item.Name}</strong> (x{item.quantity}) - ₹{(item.Price * item.quantity).toFixed(2)}</li>
                      ))
                    ) : (
                      <li>No items</li>
                    )}
                  </ul>

                  <h6 className="fw-bold mt-3">User Details:</h6>
                  <p>Name: <strong>{order.name || "N/A"}</strong></p>
                  <p>Contact: <strong>{order.contact || "N/A"}</strong></p>
                  <p>Address: <strong>{order.address || "N/A"}</strong></p>

                  <div className="d-flex flex-wrap gap-2 mt-3">
                    {order.status === "pending" && (
                      <Button variant="success" onClick={() => handleConfirmOrder(order.id)}><FaCheck className="me-1" /> Confirm</Button>
                    )}
                    {order.status === "confirmed" && (
                      <>
                        <Form.Control
                          type="text"
                          placeholder="Update Delivery Time"
                          value={deliveryTime}
                          onChange={(e) => setDeliveryTime(e.target.value)}
                          className="w-auto d-inline"
                        />
                        <Button variant="outline-light" onClick={() => handleUpdateDeliveryTime(order.id)}>Update</Button>
                      </>
                    )}
                    <Button variant="danger" onClick={() => handleDeleteOrder(order.id)}><FaTrashAlt className="me-1" /> Delete</Button>
                    {order.paymentStatus === "pending" ? (
                      <Button variant="info" onClick={() => handleUpdatePaymentStatus(order.id, "confirmed")}>Mark Paid</Button>
                    ) : (
                      <Button variant="warning" onClick={() => handleUpdatePaymentStatus(order.id, "pending")}>Mark Pending</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}